import {
  type InputMaybe,
  type DentalFormulaInput,
  ToothStatus
} from '@/_graphql/graphql-client';
import { RectangleButton } from '../../RectangleButton';
import { babyLabels } from '@/feature/phr-28/components/template/Appointment/ModalContents/DentalFormulaModal';

export interface SelectDentalFormulaLabelPropsType {
  openSearchMenuModal: () => void;
  dentalFormula: DentalFormulaInput;
}

export function ToothFormula (
  labels: string[],
  position: 'rightTop' | 'leftTop' | 'rightBottom' | 'leftBottom',
  fontColor: string = '#1976d2',
  borderColor: string = '#1976d2'
): JSX.Element[] {
  // ラベルの内容に応じてPONTIC判定（【】で囲まれている場合はPONTIC）
  return labels.map((tooth, idx) => {
    // PONTICであっても同じ色で表示（黒色）
    const style = getStyle(position, fontColor, borderColor);
    
    return (
      <span key={idx} style={style}>
        {tooth}
      </span>
    );
  });
  
  function getStyle (
    position: 'rightTop' | 'leftTop' | 'rightBottom' | 'leftBottom',
    color: string,
    border: string
  ) {
    const baseStyle = {
      display: 'inline-flex',
      alignItems: 'center',
      height: '18px',
      color,
      backgroundColor: 'transparent',
      fontSize: '0.7125rem',
      margin: '2px',
      padding: '0 8px',
    };

    switch (position) {
      case 'rightTop':
        return {
          ...baseStyle,
          borderBottom: `3px solid ${border}`,
          borderRight: `3px solid ${border}`,
        };
      case 'leftTop':
        return {
          ...baseStyle,
          borderBottom: `3px solid ${border}`,
          borderLeft: `3px solid ${border}`,
        };
      case 'rightBottom':
        return {
          ...baseStyle,
          borderTop: `3px solid ${border}`,
          borderRight: `3px solid ${border}`,
        };
      case 'leftBottom':
        return {
          ...baseStyle,
          borderTop: `3px solid ${border}`,
          borderLeft: `3px solid ${border}`,
        };
    }
  }
}

export const SelectDentalFormulaLabel: React.FC<SelectDentalFormulaLabelPropsType> = ({
  openSearchMenuModal,
  dentalFormula,
}) => {
  const label = '歯式入力';

  return (
    <>
      <RectangleButton
        label={label}
        color={'blue'}
        size={'sm'}
        disable={false}
        onClickHandler={openSearchMenuModal}
      />{' '}
      :
      {ToothFormula(formatDentalSequence(cleanTeethData(dentalFormula?.permanent?.rightTop ?? []), "rightTop"), "rightTop")}
      {ToothFormula(formatDentalSequence(cleanTeethData(dentalFormula?.permanent?.leftTop ?? []), "leftTop"), "leftTop")}
      {ToothFormula(formatDentalSequence(cleanTeethData(dentalFormula?.permanent?.rightBottom ?? []), "rightBottom"), "rightBottom")}
      {ToothFormula(formatDentalSequence(cleanTeethData(dentalFormula?.permanent?.leftBottom ?? []), "leftBottom"), "leftBottom")}
      {ToothFormula(formatBabyDentalSequence(cleanTeethData(dentalFormula?.baby?.rightTop ?? []), "rightTop"), "rightTop")}
      {ToothFormula(formatBabyDentalSequence(cleanTeethData(dentalFormula?.baby?.leftTop ?? []), "leftTop"), "leftTop")}
      {ToothFormula(formatBabyDentalSequence(cleanTeethData(dentalFormula?.baby?.rightBottom ?? []), "rightBottom"), "rightBottom")}
      {ToothFormula(formatBabyDentalSequence(cleanTeethData(dentalFormula?.baby?.leftBottom ?? []), "leftBottom"), "leftBottom")}
    </>
  );
};

export function cleanTeethData (teeth: Array<InputMaybe<boolean | ToothStatus>>): Array<ToothStatus | boolean> {
  return teeth.map((tooth) => {
    if (tooth === null || tooth === undefined) {
      return ToothStatus.False;
    }
    return tooth;
  });
}

// 歯の連番を〜で結合して返却する
export function formatDentalSequence (
  teeth: Array<ToothStatus | boolean>, 
  position: 'rightTop' | 'leftTop' | 'rightBottom' | 'leftBottom'
): string[] {
  const ranges = [];
  let start: null | number = null;
  let currentRange: Array<{ index: number; isPontic: boolean }> = [];

  // 右上や右下のデータは左から右へ1,2,3,...と格納されているが
  // 実際の歯番号としては8,7,6,...という順になるため、変換が必要
  const isRightArea = position === 'rightTop' || position === 'rightBottom';

  teeth.forEach((tooth, index) => {
    const isActive = typeof tooth === 'boolean' ? Boolean(tooth) : (tooth === ToothStatus.True || tooth === ToothStatus.Pontic);
    const isPontic = tooth === ToothStatus.Pontic;
    
    // 実際の歯番号を計算
    // マッピングは正しいので、インデックスから歯番号への変換は同じ式を使用
    let actualIndex: number = index + 1;
    
    if (isActive) {
      if (start === null) {
        start = actualIndex;
      }
      // アクティブな歯の情報を記録
      currentRange.push({ index: actualIndex, isPontic });
    } else if (!isActive && start !== null) {
      // 非アクティブになったら、これまでの範囲を処理
      ranges.push(formatComplexRange(currentRange, position));
      start = null;
      currentRange = [];
    }
  });

  // 最後の範囲を閉じる
  if (start !== null) {
    ranges.push(formatComplexRange(currentRange, position));
  }

  // 右上・右下の場合は降順（8→1）で表示するため、結果を反転させる
  if (isRightArea) {
    return ranges.reverse();
  }
  
  return ranges;
}

// 複雑な範囲を整形する関数（PONTICと通常の歯が混在する場合）
function formatComplexRange (
  range: Array<{ index: number; isPontic: boolean }>,
  position: 'rightTop' | 'leftTop' | 'rightBottom' | 'leftBottom'
): string {
  if (range.length === 0) return '';
  
  if (range.length === 1) {
    // 単一の歯の場合
    const { index, isPontic } = range[0];
    return isPontic ? `【${index}】` : `${index}`;
  }
  
  // 右上・右下の場合は降順で表示
  const isRightArea = position === 'rightTop' || position === 'rightBottom';
  
  // 範囲をソートする（右側は降順、左側は昇順）
  const sortedRange = [...range].sort((a, b) => {
    if (isRightArea) {
      return b.index - a.index; // 右側は降順
    } else {
      return a.index - b.index; // 左側は昇順
    }
  });
  
  // PONTICと通常の歯を分離せず、順番に処理する
  let result = '';
  let i = 0;
  
  while (i < sortedRange.length) {
    const current = sortedRange[i];
    
    // 現在の歯を処理
    if (current.isPontic) {
      // PONTICの歯
      result += result === '' ? `【${current.index}】` : `【${current.index}】`;
    } else {
      // 通常の歯
      // 連続する通常の歯を検索
      let j = i + 1;
      let consecutiveEnd = i;
      
      // 連続する通常の歯をチェック（連続とは隣り合った歯番号）
      while (j < sortedRange.length && !sortedRange[j].isPontic) {
        // 右側と左側で連続性の判定が異なる
        const isConsecutive = isRightArea 
          ? sortedRange[j].index === sortedRange[j-1].index - 1  // 右側（降順）: 前の歯より1小さい
          : sortedRange[j].index === sortedRange[j-1].index + 1; // 左側（昇順）: 前の歯より1大きい
        
        if (isConsecutive) {
          consecutiveEnd = j;
          j++;
        } else {
          break;
        }
      }
      
      // 連続した通常の歯を範囲表記
      if (i === consecutiveEnd) {
        // 単一の歯
        result += result === '' ? `${current.index}` : `${current.index}`;
      } else if (consecutiveEnd - i === 1) {
        // 2つだけ連続している場合は直接連結
        result += result === '' 
          ? `${sortedRange[i].index}${sortedRange[consecutiveEnd].index}` 
          : `${sortedRange[i].index}${sortedRange[consecutiveEnd].index}`;
      } else {
        // 3つ以上連続している場合は範囲表記
        result += result === '' 
          ? `${sortedRange[i].index}〜${sortedRange[consecutiveEnd].index}` 
          : `${sortedRange[i].index}〜${sortedRange[consecutiveEnd].index}`;
      }
      
      i = consecutiveEnd;
    }
    
    i++;
  }
  
  return result;
}

export function formatBabyDentalSequence (
  teeth: Array<ToothStatus | boolean>, 
  position: 'rightTop' | 'leftTop' | 'rightBottom' | 'leftBottom'
): string[] {
  const ranges = [];
  let start: number | null = null;
  let currentRange: Array<{ index: number; isPontic: boolean }> = [];

  // 右上や右下のデータは左から右へA,B,C,D,Eと格納されている
  const isRightArea = position === 'rightTop' || position === 'rightBottom';

  teeth.forEach((tooth, index) => {
    const isActive = typeof tooth === 'boolean' ? Boolean(tooth) : (tooth === ToothStatus.True || tooth === ToothStatus.Pontic);
    const isPontic = tooth === ToothStatus.Pontic;
    
    // 実際の歯のインデックスを計算
    // マッピングは正しいので、同じ計算式を使用
    let actualIndex: number = index;
    
    if (isActive) {
      if (start === null) {
        start = actualIndex;
      }
      // アクティブな歯の情報を記録
      currentRange.push({ index: actualIndex, isPontic });
    } else if (!isActive && start !== null) {
      // 非アクティブになったら、これまでの範囲を処理
      ranges.push(formatComplexBabyRange(currentRange, position));
      start = null;
      currentRange = [];
    }
  });

  // 最後の範囲を閉じる
  if (start !== null) {
    ranges.push(formatComplexBabyRange(currentRange, position));
  }

  // 右上・右下の場合は降順（E→A）で表示するため、結果を反転させる
  if (isRightArea) {
    return ranges.reverse();
  }
  
  return ranges;
}

// 複雑な乳歯範囲を整形する関数
function formatComplexBabyRange (
  range: Array<{ index: number; isPontic: boolean }>,
  position: 'rightTop' | 'leftTop' | 'rightBottom' | 'leftBottom'
): string {
  if (range.length === 0) return '';
  
  if (range.length === 1) {
    // 単一の歯の場合
    const { index, isPontic } = range[0];
    return isPontic ? `【${babyLabels[index]}】` : `${babyLabels[index]}`;
  }
  
  // 右上・右下の場合は降順で表示
  const isRightArea = position === 'rightTop' || position === 'rightBottom';
  
  // 範囲をソートする（右側は降順、左側は昇順）
  const sortedRange = [...range].sort((a, b) => {
    if (isRightArea) {
      return b.index - a.index; // 右側は降順
    } else {
      return a.index - b.index; // 左側は昇順
    }
  });
  
  // PONTICと通常の歯を分離せず、順番に処理する
  let result = '';
  let i = 0;
  
  while (i < sortedRange.length) {
    const current = sortedRange[i];
    
    // 現在の歯を処理
    if (current.isPontic) {
      // PONTICの歯
      result += result === '' ? `【${babyLabels[current.index]}】` : `【${babyLabels[current.index]}】`;
    } else {
      // 通常の歯
      // 連続する通常の歯を検索
      let j = i + 1;
      let consecutiveEnd = i;
      
      // 連続する通常の歯をチェック（連続とは隣り合った歯番号）
      while (j < sortedRange.length && !sortedRange[j].isPontic) {
        // 右側と左側で連続性の判定が異なる
        const isConsecutive = isRightArea 
          ? sortedRange[j].index === sortedRange[j-1].index - 1  // 右側（降順）: 前の歯より1小さい
          : sortedRange[j].index === sortedRange[j-1].index + 1; // 左側（昇順）: 前の歯より1大きい
        
        if (isConsecutive) {
          consecutiveEnd = j;
          j++;
        } else {
          break;
        }
      }
      
      // 連続した通常の歯を範囲表記
      if (i === consecutiveEnd) {
        // 単一の歯
        result += result === '' ? `${babyLabels[current.index]}` : `${babyLabels[current.index]}`;
      } else if (consecutiveEnd - i === 1) {
        // 2つだけ連続している場合は直接連結
        result += result === '' 
          ? `${babyLabels[sortedRange[i].index]}${babyLabels[sortedRange[consecutiveEnd].index]}` 
          : `${babyLabels[sortedRange[i].index]}${babyLabels[sortedRange[consecutiveEnd].index]}`;
      } else {
        // 3つ以上連続している場合は範囲表記
        result += result === '' 
          ? `${babyLabels[sortedRange[i].index]}〜${babyLabels[sortedRange[consecutiveEnd].index]}` 
          : `${babyLabels[sortedRange[i].index]}〜${babyLabels[sortedRange[consecutiveEnd].index]}`;
      }
      
      i = consecutiveEnd;
    }
    
    i++;
  }
  
  return result;
}